<template>
  <div class="auth-page">
    <div class="auth-bg"></div>
    <div class="auth-grid"></div>

    <div class="auth-container">
      <div class="auth-card">
        <!-- Header -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="auth-logo-icon">CS</div>
            <span class="auth-logo-text">Casino<span class="accent">Script</span></span>
          </div>
          <div class="verify-icon-wrap">
            <svg viewBox="0 0 24 24" fill="currentColor" class="verify-icon">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
          </div>
          <h1 class="auth-title">{{ $t('auth.verify_email_title') }}</h1>
          <p class="auth-subtitle">{{ $t('auth.verify_email_subtitle') }}</p>
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="auth-success">
          <svg class="auth-success-icon-svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ successMessage }}</span>
        </div>

        <!-- Error Alert -->
        <div v-if="errorMessage" class="auth-error">
          <span class="auth-error-icon">!</span>
          <div class="auth-error-content">
            <span>{{ errorMessage }}</span>
            <span v-if="attemptsRemaining !== null" class="auth-error-attempts">
              {{ attemptsRemaining === 1 ? $t('auth.verify_email_attempt_singular', { n: attemptsRemaining }) : $t('auth.verify_email_attempts_plural', { n: attemptsRemaining }) }}
            </span>
          </div>
        </div>

        <!-- Code Input -->
        <form class="auth-form" @submit.prevent="handleVerify">
          <div class="form-group">
            <label class="form-label">{{ $t('auth.verify_email_code_label') }}</label>
            <div class="code-boxes">
              <input
                v-for="(_, i) in digits"
                :key="i"
                :ref="(el) => setDigitRef(i, el as HTMLInputElement | null)"
                v-model="digits[i]"
                type="text"
                inputmode="numeric"
                maxlength="1"
                class="code-box"
                :class="{ filled: digits[i] !== '' }"
                autocomplete="one-time-code"
                @input="onDigitInput(i)"
                @keydown="onDigitKeydown(i, $event)"
                @paste="onPaste"
                @focus="onDigitFocus(i)"
              />
            </div>
          </div>

          <!-- Verify Button -->
          <button type="submit" class="btn-verify" :disabled="isLoading || code.length < 6">
            <span v-if="isLoading" class="btn-spinner"></span>
            <span v-else>{{ $t('auth.verify_email_button') }}</span>
          </button>
        </form>

        <!-- Resend Section -->
        <div class="resend-section">
          <p class="resend-text">{{ $t('auth.verify_email_no_code') }}</p>
          <button
            class="resend-btn"
            :disabled="resendCooldown > 0 || isResending"
            @click="handleResend"
          >
            <span v-if="isResending" class="btn-spinner btn-spinner-small"></span>
            <template v-else-if="resendCooldown > 0">
              {{ $t('auth.verify_email_resend_countdown', { seconds: resendCooldown }) }}
            </template>
            <template v-else>
              {{ $t('auth.verify_email_resend') }}
            </template>
          </button>
        </div>

        <!-- Footer -->
        <div class="auth-footer">
          <router-link to="/player/lobby" class="skip-link">{{ $t('auth.verify_email_skip') }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import apiClient from '@/api/client'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()

const digits = ref<string[]>(['', '', '', '', '', ''])
const digitRefs: (HTMLInputElement | null)[] = []
const isLoading = ref(false)
const isResending = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const attemptsRemaining = ref<number | null>(null)
const resendCooldown = ref(0)
let cooldownTimer: ReturnType<typeof setInterval> | null = null

const code = computed(() => digits.value.join(''))

function setDigitRef(index: number, el: HTMLInputElement | null) {
  digitRefs[index] = el
}

function onDigitInput(index: number) {
  const value = digits.value[index]

  // Only allow numeric input
  if (value && !/^\d$/.test(value)) {
    digits.value[index] = ''
    return
  }

  // Auto-advance to next box
  if (value && index < 5) {
    digitRefs[index + 1]?.focus()
  }

  // Auto-submit when all 6 digits filled
  if (code.value.length === 6) {
    handleVerify()
  }
}

function onDigitKeydown(index: number, event: KeyboardEvent) {
  // Backspace: clear current and move to previous
  if (event.key === 'Backspace') {
    if (digits.value[index] === '' && index > 0) {
      digitRefs[index - 1]?.focus()
      digits.value[index - 1] = ''
    }
  }

  // Left arrow
  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault()
    digitRefs[index - 1]?.focus()
  }

  // Right arrow
  if (event.key === 'ArrowRight' && index < 5) {
    event.preventDefault()
    digitRefs[index + 1]?.focus()
  }
}

function onDigitFocus(index: number) {
  // Select content on focus for easy overwrite
  digitRefs[index]?.select()
}

function onPaste(event: ClipboardEvent) {
  event.preventDefault()
  const pasted = event.clipboardData?.getData('text')?.trim() ?? ''
  const cleaned = pasted.replace(/\D/g, '').slice(0, 6)

  if (cleaned.length === 0) return

  for (let i = 0; i < 6; i++) {
    digits.value[i] = cleaned[i] ?? ''
  }

  // Focus the next empty box or the last box
  const nextEmpty = digits.value.findIndex((d) => d === '')
  const focusIndex = nextEmpty === -1 ? 5 : nextEmpty
  digitRefs[focusIndex]?.focus()

  // Auto-submit if full code pasted
  if (cleaned.length === 6) {
    handleVerify()
  }
}

function clearCode() {
  for (let i = 0; i < 6; i++) {
    digits.value[i] = ''
  }
  digitRefs[0]?.focus()
}

function startCooldown() {
  resendCooldown.value = 60
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      if (cooldownTimer) {
        clearInterval(cooldownTimer)
        cooldownTimer = null
      }
    }
  }, 1000)
}

async function handleVerify() {
  if (code.value.length < 6 || isLoading.value) return

  errorMessage.value = ''
  successMessage.value = ''
  attemptsRemaining.value = null
  isLoading.value = true

  try {
    await apiClient.post('/api/auth/verify-email', { code: code.value })

    // Refresh profile to update email_verified_at
    await userStore.fetchProfile()

    successMessage.value = t('auth.verify_email_success')

    setTimeout(() => {
      router.push('/player/lobby')
    }, 1500)
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string; attempts_remaining?: number }; status?: number } }
    const status = axiosError.response?.status
    const data = axiosError.response?.data

    if (status === 422) {
      errorMessage.value = data?.message || t('auth.verify_error_invalid_code')
      if (data?.attempts_remaining !== undefined) {
        attemptsRemaining.value = data.attempts_remaining
      }
    } else if (status === 401) {
      errorMessage.value = t('auth.verify_error_session_expired')
      router.push('/login')
    } else {
      errorMessage.value = data?.message || t('auth.error_generic')
    }

    clearCode()
  } finally {
    isLoading.value = false
  }
}

async function handleResend() {
  if (resendCooldown.value > 0 || isResending.value) return

  errorMessage.value = ''
  successMessage.value = ''
  attemptsRemaining.value = null
  isResending.value = true

  try {
    await apiClient.post('/api/auth/resend-verification')
    successMessage.value = t('auth.verify_email_code_resent')
    startCooldown()
    clearCode()
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string }; status?: number } }
    const status = axiosError.response?.status

    if (status === 429) {
      errorMessage.value = t('auth.verify_error_resend_wait')
      startCooldown()
    } else {
      errorMessage.value = axiosError.response?.data?.message || t('auth.verify_error_resend_failed')
    }
  } finally {
    isResending.value = false
  }
}

onMounted(() => {
  // Auto-focus first digit box
  digitRefs[0]?.focus()
  // Start cooldown (user just registered and was sent an initial code)
  startCooldown()
})

onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
  }
})
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(0, 231, 1, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(20, 117, 225, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.auth-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: var(--space-5);
}

.auth-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.95) 0%, rgba(33, 55, 67, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.6s ease both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.auth-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.auth-logo-icon {
  width: 40px;
  height: 40px;
  background: var(--accent-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-family-display);
  box-shadow: var(--shadow-glow-green);
}

.auth-logo-text {
  font-family: var(--font-family-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.auth-logo-text .accent {
  color: var(--accent-primary);
}

.verify-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-4);
}

.verify-icon {
  width: 56px;
  height: 56px;
  color: var(--accent-secondary);
}

.auth-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.auth-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
  line-height: 1.5;
}

/* Success Alert */
.auth-success {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: rgba(0, 231, 1, 0.08);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: var(--radius-md);
  color: var(--accent-primary);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-success-icon-svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

/* Error Alert */
.auth-error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-light);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-error-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.auth-error-attempts {
  font-size: var(--font-size-sm);
  opacity: 0.8;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  margin-bottom: var(--space-6);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  text-align: center;
}

/* Code Boxes */
.code-boxes {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
}

.code-box {
  width: 48px;
  height: 56px;
  background: rgba(15, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 700;
  font-family: var(--font-family-display);
  text-align: center;
  transition: all var(--transition-fast);
  caret-color: var(--accent-primary);
}

.code-box:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
  background: rgba(15, 25, 35, 1);
  box-shadow: 0 0 0 2px rgba(20, 117, 225, 0.15);
}

.code-box.filled {
  border-color: var(--accent-primary);
  background: rgba(0, 231, 1, 0.04);
}

/* Verify Button */
.btn-verify {
  width: 100%;
  padding: 12px 16px;
  background: var(--accent-primary);
  color: var(--color-on-accent);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-primary);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
}

.btn-verify:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-2px);
}

.btn-verify:active:not(:disabled) {
  transform: translateY(0);
}

.btn-verify:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

.btn-spinner-small {
  width: 14px;
  height: 14px;
  border-width: 1.5px;
  border-color: rgba(255, 255, 255, 0.2);
  border-top-color: currentColor;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Resend Section */
.resend-section {
  text-align: center;
  margin-bottom: var(--space-6);
}

.resend-text {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
  margin-bottom: var(--space-2);
}

.resend-btn {
  background: none;
  border: none;
  color: var(--accent-secondary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0;
}

.resend-btn:hover:not(:disabled) {
  color: var(--text-primary);
  text-decoration: underline;
}

.resend-btn:disabled {
  color: var(--text-tertiary);
  cursor: not-allowed;
  text-decoration: none;
}

/* Footer */
.auth-footer {
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

.skip-link {
  color: var(--text-tertiary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.skip-link:hover {
  color: var(--text-secondary);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 28px;
  }

  .auth-title {
    font-size: 20px;
  }

  .code-box {
    width: 42px;
    height: 50px;
    font-size: 20px;
  }

  .code-boxes {
    gap: 6px;
  }
}
</style>
